/**
 * company-verdict.ts — the company home's "overall status" engine (Home Redesign).
 *
 * Pure + deterministic: same signals → same verdict. Produces the one-line
 * plain-English status at the top of `/` (Direction A + C), plus a "Tier-2"
 * gap-closer (ranked options to fix an at-risk cash/payroll state).
 *
 * Honesty rules (per docs/METHODS-AND-COMPLIANCE-FOUNDATION.md):
 *  - Never assert "you can't make payroll" off unknown/zero cash. If cash isn't
 *    bank-true (no reconciled statement), we DON'T raise the cash alarm — we ask
 *    the user to reconcile.
 *  - Worst material signal wins. The subline carries the proof (the numbers).
 *  - Options, not directives. Gap-closers are suggestions the user chooses.
 *
 * No React, no DOM — unit-tested from source (vitest is a deploy gate).
 */

export type VerdictTier = 'at_risk' | 'watch' | 'healthy' | 'setup';

export interface CompanySignals {
  /** Bank-true cash on hand in cents (forecast[0].runningBalance × 100), or null if unknown. */
  cashCents: number | null;
  /** True only when cash is bank-true (a statement is reconciled). Gates the cash alarm. */
  cashKnown: boolean;
  /** Lowest projected weekly running balance over the forecast, in cents (or null). */
  lowestProjectedCents: number | null;
  /** Next payroll obligation in cents + ISO date, if known. */
  payrollDueCents?: number | null;
  payrollDueDate?: string | null;
  /** Counts from nav-counts. */
  overdueBills: number;
  complianceDueSoon: number;
  pendingApprovals: number;
  /** Expenses still needing docs/categorization (count) — protects deductions. */
  docsToFix?: number;
  /** Billing entities: overdue A/R in cents (60+ days), if known. */
  overdueArCents?: number | null;
  operatingModel: 'paid_for_work' | 'billing';
  /**
   * True for a brand-new tenant with no financial activity yet (gated by the
   * backend first-run window: entity < 60d AND required setup items remain).
   * Flips the verdict from a graded "on track / HEALTHY" to a guiding "let's
   * finish setting up" tone so a zero-data home doesn't read as "all handled".
   */
  firstRun?: boolean;
  /** Compliance deadlines already tracked from onboarding (surfaced as a payoff). */
  trackedDeadlines?: number;
}

export interface GapCloser {
  label: string;
  detail: string;
  route: string;
}

export interface CompanyVerdict {
  tier: VerdictTier;
  /** The one-line status sentence. */
  statement: string;
  /** Supporting proof line (the numbers behind the verdict). */
  subline: string;
  /** Ranked options to close an at-risk gap (empty unless at_risk and closers exist). */
  gapClosers: GapCloser[];
  /** Machine-readable reasons (for testing / "why am I seeing this"). */
  reasons: string[];
}

const PILL: Record<VerdictTier, string> = {
  at_risk: 'AT RISK',
  watch: 'WATCH',
  healthy: 'HEALTHY',
  setup: 'GETTING SET UP',
};

export function verdictPillLabel(tier: VerdictTier): string {
  return PILL[tier];
}

function fmtUsd(cents: number): string {
  const d = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Math.abs(d) >= 1000 ? 0 : 2,
    maximumFractionDigits: Math.abs(d) >= 1000 ? 0 : 2,
  }).format(d);
}

/**
 * Compute the company's overall status verdict. Worst material signal wins.
 * Deterministic + total — tolerates partial/missing signals.
 */
export function computeCompanyVerdict(s: CompanySignals): CompanyVerdict {
  const reasons: string[] = [];

  // ---- AT RISK: a real, bank-true projected cash gap (never off unknown cash) ----
  const cashGap =
    s.cashKnown &&
    s.lowestProjectedCents != null &&
    s.lowestProjectedCents < 0;

  if (cashGap) {
    reasons.push('projected_cash_gap');
    const gap = Math.abs(s.lowestProjectedCents as number);
    const payrollPhrase =
      s.payrollDueCents && s.payrollDueDate
        ? ` Payroll of ${fmtUsd(s.payrollDueCents)} is due ${s.payrollDueDate}.`
        : '';
    return {
      tier: 'at_risk',
      statement: 'Heads up — your projected cash goes negative soon.',
      subline: `Cash on hand ${s.cashCents != null ? fmtUsd(s.cashCents) : '—'} · projected short by ${fmtUsd(gap)} at the low point.${payrollPhrase}`,
      gapClosers: buildGapClosers(s, gap),
      reasons,
    };
  }

  // Cash genuinely unknown (no reconciled statement) — prompt, don't alarm.
  const cashUnknown = !s.cashKnown || s.cashCents == null;
  if (cashUnknown) reasons.push('cash_unreconciled');

  // ---- SETUP: a brand-new tenant with no financial activity. Guide, don't grade.
  // Runs after the real cash-gap check (a no-cash tenant can't trip it) but BEFORE
  // watch — for a fresh entity, auto-seeded compliance deadlines are a setup payoff,
  // not an alarm. A green "On track / HEALTHY" on an empty account reads as
  // "you're all set, nothing to do" and suppresses the very setup the user needs.
  if (s.firstRun) {
    reasons.push('first_run');
    const n = s.trackedDeadlines ?? s.complianceDueSoon ?? 0;
    return {
      tier: 'setup',
      statement: 'Let’s finish setting up your practice.',
      subline: n > 0
        ? `Your compliance calendar is live — ${n} deadline${n === 1 ? '' : 's'} already tracked. Capture your first expense to bring your numbers to life.`
        : 'Capture your first expense or reconcile a bank statement to bring your numbers to life.',
      gapClosers: [],
      reasons,
    };
  }

  // ---- WATCH: material money/compliance pressure, but no cash gap ----
  const watchReasons: string[] = [];
  if ((s.overdueArCents ?? 0) > 0) watchReasons.push('overdue_ar');
  if (s.overdueBills > 0) watchReasons.push('overdue_bills');
  if (s.complianceDueSoon > 0) watchReasons.push('compliance_due');
  if ((s.docsToFix ?? 0) > 0) watchReasons.push('docs_to_fix');
  if (s.pendingApprovals > 0) watchReasons.push('pending_approvals');

  if (watchReasons.length > 0) {
    reasons.push(...watchReasons);
    const { statement, subline } = watchCopy(s, watchReasons);
    return { tier: 'watch', statement, subline, gapClosers: [], reasons };
  }

  // ---- HEALTHY ----
  reasons.push('all_clear');
  return {
    tier: 'healthy',
    statement: cashUnknown ? 'On track. Reconcile your bank to confirm cash.' : 'On track. Nothing needs you right now.',
    subline: cashUnknown
      ? 'No overdue bills, A/R, or compliance items. Bank-true cash will light up once a statement is reconciled.'
      : `Cash on hand ${s.cashCents != null ? fmtUsd(s.cashCents) : '—'} · no overdue items · compliance current.`,
    gapClosers: [],
    reasons,
  };
}

/** Pick the worst watch reason for the headline; list the rest in the subline. */
function watchCopy(s: CompanySignals, reasons: string[]): { statement: string; subline: string } {
  const parts: string[] = [];
  if (reasons.includes('overdue_ar') && (s.overdueArCents ?? 0) > 0)
    parts.push(`${fmtUsd(s.overdueArCents as number)} A/R 60+ days overdue`);
  if (reasons.includes('overdue_bills')) parts.push(`${s.overdueBills} bill${s.overdueBills === 1 ? '' : 's'} overdue`);
  if (reasons.includes('compliance_due')) parts.push(`${s.complianceDueSoon} compliance item${s.complianceDueSoon === 1 ? '' : 's'} due soon`);
  if (reasons.includes('docs_to_fix')) parts.push(`${s.docsToFix} expense${s.docsToFix === 1 ? '' : 's'} need docs`);
  if (reasons.includes('pending_approvals')) parts.push(`${s.pendingApprovals} pending approval${s.pendingApprovals === 1 ? '' : 's'}`);

  // Headline by the most material lever for the entity's model.
  let statement: string;
  if (reasons.includes('overdue_ar')) statement = 'Collections need a nudge. Cash is otherwise stable.';
  else if (reasons.includes('overdue_bills')) statement = 'A few bills are overdue — worth clearing.';
  else if (reasons.includes('compliance_due')) statement = 'Mostly good — a compliance item needs attention.';
  else if (reasons.includes('docs_to_fix')) statement = 'Doing well — a couple of expenses need docs.';
  else statement = 'Doing well — a few items are waiting on you.';

  const cashLead = s.cashKnown && s.cashCents != null ? `Cash ${fmtUsd(s.cashCents)} · ` : '';
  return { statement, subline: cashLead + parts.join(' · ') };
}

/**
 * Tier-2 gap-closers: ranked, deterministic OPTIONS to close a projected cash gap.
 * Each is a suggestion (the user decides + acts) — never auto-executed.
 */
function buildGapClosers(s: CompanySignals, gapCents: number): GapCloser[] {
  const closers: GapCloser[] = [];
  // 1) Collect overdue A/R (fastest cash, customer owes you).
  if ((s.overdueArCents ?? 0) > 0) {
    closers.push({
      label: 'Collect overdue invoices',
      detail: `${fmtUsd(s.overdueArCents as number)} is past due — collecting it covers ${Math.min(100, Math.round(((s.overdueArCents as number) / gapCents) * 100))}% of the gap.`,
      route: '/invoices/aging',
    });
  }
  // 2) Review/defer upcoming bills.
  if (s.overdueBills > 0) {
    closers.push({
      label: 'Review bills due soon',
      detail: `${s.overdueBills} bill${s.overdueBills === 1 ? '' : 's'} overdue — confirm timing or defer non-critical ones.`,
      route: '/finance/bills',
    });
  }
  // 3) Reconcile to confirm the true picture.
  closers.push({
    label: 'Check the cash-flow forecast',
    detail: 'See the week-by-week projection and what is driving the dip.',
    route: '/finance/cashflow',
  });
  return closers;
}
